import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation } from 'convex/react';
import { api } from '@/lib/api';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Icons } from '@/components/ui/icons';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { VerificationStatusBadge, DocumentStatusBadge } from '@/components/features/verification/VerificationStatusBadge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@template/lib/toast';
import { UnapproveUserDialog } from '@/components/operators/unapprove-user-dialog';
import { formatBerlinDateTime, formatDateOnlyTimestamp, isDateOnlyExpired } from '@/lib/berlin-time';

const OperatorVerificationDetail = () => {
  const { t, i18n } = useTranslation('operator');
  const { userId } = Route.useParams();
  const navigate = useNavigate();

  const details = useQuery(api.app.verificationOperator.getSubstituteVerificationDetails, {
    userId: userId as any,
  });

  // Action mutations
  const approveVerification = useMutation(api.app.verificationOperator.approveVerification);
  const requestChanges = useMutation(api.app.verificationOperator.requestVerificationChanges);
  const rejectVerification = useMutation(api.app.verificationOperator.rejectVerification);
  const sendReminder = useMutation(api.app.verificationOperator.sendInsuranceReminder);

  // Modal states
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showChangesModal, setShowChangesModal] = useState(false);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [showUnapproveModal, setShowUnapproveModal] = useState(false);

  // Form states
  const [approveNotes, setApproveNotes] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [rejectDocType, setRejectDocType] = useState<'approbation' | 'insurance' | 'both'>('both');
  const [changesNotes, setChangesNotes] = useState('');
  const [changesDocType, setChangesDocType] = useState<'approbation' | 'insurance' | 'both'>('both');
  const [reminderMessage, setReminderMessage] = useState('');
  const [processing, setProcessing] = useState(false);

  // Current document preview
  const [currentDocView, setCurrentDocView] = useState<'approbation' | 'insurance'>('approbation');

  if (!details) {
    return (
      <div className='flex items-center justify-center min-h-screen'>
        <Icons.Loader className='h-8 w-8 animate-spin text-muted-foreground' />
      </div>
    );
  }

  const { user, profile, approbation, insurance, verificationHistory } = details;

  const formatDate = (timestamp: number) => {
    return formatBerlinDateTime(timestamp, i18n.language || 'de-DE');
  };

  const handleApprove = async () => {
    setProcessing(true);
    try {
      await approveVerification({
        userId: userId as any,
        ...(approveNotes ? { notes: approveNotes } : {}),
      });
      toast.success(t('verification.verificationDetail.toasts.approved'));
      setShowApproveModal(false);
      setApproveNotes('');
      void navigate({ to: '/operators/verification' });
    } catch (error: any) {
      toast.error(error?.message || t('verification.verificationDetail.toasts.approveFailed'));
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error(t('verification.verificationDetail.toasts.rejectionReasonRequired'));
      return;
    }

    setProcessing(true);
    try {
      await rejectVerification({
        userId: userId as any,
        documentType: rejectDocType,
        reason: rejectReason,
      });
      toast.success(t('verification.verificationDetail.toasts.rejected'));
      setShowRejectModal(false);
      setRejectReason('');
      void navigate({ to: '/operators/verification' });
    } catch (error: any) {
      toast.error(error?.message || t('verification.verificationDetail.toasts.rejectFailed'));
    } finally {
      setProcessing(false);
    }
  };

  const handleRequestChanges = async () => {
    if (!changesNotes.trim()) {
      toast.error(t('verification.verificationDetail.toasts.changeCommentsRequired'));
      return;
    }

    setProcessing(true);
    try {
      await requestChanges({
        userId: userId as any,
        documentType: changesDocType,
        notes: changesNotes,
      });
      toast.success(t('verification.verificationDetail.toasts.changesRequested'));
      setShowChangesModal(false);
      setChangesNotes('');
      void navigate({ to: '/operators/verification' });
    } catch (error: any) {
      toast.error(error?.message || t('verification.verificationDetail.toasts.requestChangesFailed'));
    } finally {
      setProcessing(false);
    }
  };

  const handleSendReminder = async () => {
    setProcessing(true);
    try {
      await sendReminder({
        userId: userId as any,
        ...(reminderMessage ? { customMessage: reminderMessage } : {}),
      });
      toast.success(t('verification.verificationDetail.toasts.reminderSent'));
      setShowReminderModal(false);
      setReminderMessage('');
    } catch (error: any) {
      toast.error(error?.message || t('verification.verificationDetail.toasts.reminderFailed'));
    } finally {
      setProcessing(false);
    }
  };

  const currentDoc = currentDocView === 'approbation' ? approbation : insurance;
  const isExpired = insurance ? isDateOnlyExpired(insurance.validTo) : false;

  return (
    <div className='p-4 sm:p-6 lg:p-8 space-y-6'>
      {/* Header */}
      <div className='flex items-center justify-between'>
        <div className='flex items-center gap-4'>
          <Button variant='ghost' size='sm' onClick={() => navigate({ to: '/operators/verification' })}>
            <Icons.ArrowLeft className='h-4 w-4 mr-2' />
            {t('verification.verificationDetail.backToQueue')}
          </Button>
          <div>
            <h1 className='text-2xl font-semibold'>
              {user.firstName} {user.lastName}
            </h1>
            <p className='text-sm text-muted-foreground'>{user.email}</p>
          </div>
        </div>
        <VerificationStatusBadge status={profile.verificationStatus as any} />
      </div>

      <div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
        {/* Left Panel: User Info & Actions */}
        <div className='space-y-6'>
          {/* User Information */}
          <Card>
            <CardHeader>
              <CardTitle className='text-base'>{t('verification.verificationDetail.representativeInfo.title')}</CardTitle>
            </CardHeader>
            <CardContent className='space-y-3 text-sm'>
              <div>
                <p className='text-muted-foreground'>{t('verification.verificationDetail.representativeInfo.specialization')}</p>
                <p>{profile.specialization.join(', ') || '—'}</p>
              </div>
              <div>
                <p className='text-muted-foreground'>{t('verification.verificationDetail.representativeInfo.registered')}</p>
                <p>{user.createdAt ? formatDate(user.createdAt) : '—'}</p>
              </div>
              {profile.insuranceExpiresAt && (
                <div>
                  <p className='text-muted-foreground'>{t('verification.verificationDetail.representativeInfo.insuranceExpires')}</p>
                  <p className={isExpired ? 'text-destructive font-semibold' : ''}>
                    {formatDateOnlyTimestamp(profile.insuranceExpiresAt, i18n.language || 'de-DE')}
                    {isExpired && ` ${t('verification.verificationDetail.representativeInfo.expiredSuffix')}`}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Documents Overview */}
          <Card>
            <CardHeader>
              <CardTitle className='text-base'>{t('verification.verificationDetail.documents.title')}</CardTitle>
            </CardHeader>
            <CardContent className='space-y-3'>
              <div className='flex items-center justify-between'>
                <span className='text-sm'>{t('documents.fields.approbation')}</span>
                {approbation ? <DocumentStatusBadge verified={approbation.verified} /> : <Badge variant='outline'>{t('verification.verificationDetail.documents.notUploaded')}</Badge>}
              </div>
              <div className='flex items-center justify-between'>
                <span className='text-sm'>{t('documents.insurance')}</span>
                {insurance ? <DocumentStatusBadge verified={insurance.verified} expiresAt={insurance.validTo} /> : <Badge variant='outline'>{t('verification.verificationDetail.documents.notUploaded')}</Badge>}
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <Card>
            <CardHeader>
              <CardTitle className='text-base'>{t('verification.verificationDetail.actions.title')}</CardTitle>
            </CardHeader>
            <CardContent className='space-y-2'>
              <Button
                className='w-full'
                onClick={() => setShowApproveModal(true)}
                disabled={!approbation || !insurance || profile.verificationStatus === 'verified'}
                title={profile.verificationStatus === 'unapproved' ? t('verification.verificationDetail.actions.reapproveTooltip') : undefined}
              >
                <Icons.CheckCircle className='h-4 w-4 mr-2' />
                {profile.verificationStatus === 'unapproved' ? t('verification.verificationDetail.actions.reapproveUser') : t('verification.verificationDetail.actions.approveAll')}
              </Button>
              <Button className='w-full' variant='outline' onClick={() => setShowChangesModal(true)} disabled={!approbation && !insurance}>
                <Icons.AlertCircle className='h-4 w-4 mr-2' />
                {t('verification.verificationDetail.actions.requestChanges')}
              </Button>
              <Button className='w-full' variant='destructive' onClick={() => setShowRejectModal(true)} disabled={!approbation && !insurance}>
                <Icons.XCircle className='h-4 w-4 mr-2' />
                {t('verification.verificationDetail.actions.reject')}
              </Button>

              {profile.verificationStatus === 'verified' && (
                <Button className='w-full' variant='destructive' onClick={() => setShowUnapproveModal(true)}>
                  <Icons.AlertCircle className='h-4 w-4 mr-2' />
                  {t('verification.actions.unapproveUser')}
                </Button>
              )}

              {isExpired && (
                <Button className='w-full' variant='secondary' onClick={() => setShowReminderModal(true)}>
                  <Icons.Mail className='h-4 w-4 mr-2' />
                  {t('verification.verificationDetail.actions.sendReminder')}
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Unapproval Details */}
          {profile.verificationStatus === 'unapproved' && (
            <>
              <Alert variant='destructive' className='border-destructive'>
                <Icons.AlertTriangle className='h-4 w-4' />
                <AlertDescription className='font-medium'>{t('verification.verificationDetail.unapproval.warning')}</AlertDescription>
              </Alert>

              <Card className='border-destructive'>
                <CardHeader>
                  <CardTitle className='text-base text-destructive'>{t('verification.verificationDetail.unapproval.title')}</CardTitle>
                </CardHeader>
                <CardContent className='space-y-4'>
                  <div className='flex items-start gap-2'>
                    <Badge variant='destructive' className='mt-0.5'>
                      {t('verification.status.unapproved')}
                    </Badge>
                  </div>

                  <div className='space-y-3 text-sm'>
                    <div>
                      <p className='text-muted-foreground mb-1'>{t('verification.verificationDetail.unapproval.userSeesThis')}</p>
                      <div className='bg-destructive/10 border border-destructive/20 rounded-md p-3'>
                        <p className='text-foreground'>{profile.unapprovalReason || t('verification.verificationDetail.unapproval.noReasonProvided')}</p>
                      </div>
                    </div>

                    <div>
                      <p className='text-destructive mb-1 font-medium'>{t('verification.verificationDetail.unapproval.internalOnly')}</p>
                      <div className='bg-muted/50 border rounded-md p-3'>
                        <p className='text-foreground'>{profile.unapprovalInternalNotes || t('verification.verificationDetail.unapproval.noInternalNotes')}</p>
                      </div>
                    </div>

                    <div className='pt-2 border-t space-y-2'>
                      <div className='flex items-center justify-between'>
                        <span className='text-muted-foreground'>{t('verification.verificationDetail.unapproval.unapprovedOn')}</span>
                        <span className='font-medium'>{profile.unapprovedAt ? formatDate(profile.unapprovedAt) : '—'}</span>
                      </div>
                      <div className='flex items-center justify-between'>
                        <span className='text-muted-foreground'>{t('verification.verificationDetail.unapproval.unapprovedBy')}</span>
                        <span className='font-medium'>{profile.unapprovedByName || t('verification.verificationDetail.unapproval.unknownOperator')}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          )}

          {/* Verification History */}
          {verificationHistory.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className='text-base'>{t('verification.verificationHistory')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className='space-y-3'>
                  {verificationHistory.map((entry: any, idx: number) => (
                    <div key={idx} className='text-sm border-l-2 pl-3 py-1'>
                      <div className='flex items-center gap-2'>
                        <Badge variant='outline' className='text-xs'>
                          {t(`history.${entry.action}`, { defaultValue: entry.action })}
                        </Badge>
                        <span className='text-xs text-muted-foreground'>{t(`verification.verificationDetail.docTypes.${entry.documentType}`, { defaultValue: entry.documentType })}</span>
                      </div>
                      <p className='text-xs text-muted-foreground mt-1'>{t('verification.verificationDetail.history.byOperator', { operator: entry.operatorName, date: formatDate(entry.createdAt) })}</p>
                      {entry.notes && <p className='text-xs mt-1'>{entry.notes}</p>}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right Panel: Document Viewer */}
        <div className='lg:col-span-2'>
          <Card className='h-full'>
            <CardHeader>
              <div className='flex items-center justify-between'>
                <CardTitle className='text-base'>{t('verification.verificationDetail.viewer.title')}</CardTitle>
                <div className='flex gap-2'>
                  {approbation && (
                    <Button size='sm' variant={currentDocView === 'approbation' ? 'default' : 'outline'} onClick={() => setCurrentDocView('approbation')}>
                      {t('documents.fields.approbation')}
                    </Button>
                  )}
                  {insurance && (
                    <Button size='sm' variant={currentDocView === 'insurance' ? 'default' : 'outline'} onClick={() => setCurrentDocView('insurance')}>
                      {t('documents.insurance')}
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {!currentDoc && (
                <div className='flex items-center justify-center py-12 text-muted-foreground'>
                  <div className='text-center'>
                    <Icons.FileText className='h-12 w-12 mx-auto mb-3 opacity-30' />
                    <p>{t('verification.verificationDetail.viewer.noDocument')}</p>
                  </div>
                </div>
              )}

              {currentDoc && (
                <div className='space-y-4'>
                  {/* Document Info */}
                  <div className='bg-muted/30 p-4 rounded-lg space-y-2 text-sm'>
                    <div className='flex items-center justify-between'>
                      <span className='text-muted-foreground'>{t('verification.verificationDetail.viewer.fileName')}</span>
                      <span className='font-medium'>{currentDocView === 'approbation' ? approbation?.name : `${insurance?.company}_${insurance?.policyNumber}.pdf`}</span>
                    </div>
                    <div className='flex items-center justify-between'>
                      <span className='text-muted-foreground'>{t('verification.verificationDetail.viewer.uploaded')}</span>
                      <span>{formatDate(currentDoc.uploadedAt)}</span>
                    </div>
                    {currentDocView === 'insurance' && insurance && (
                      <>
                        <div className='flex items-center justify-between'>
                          <span className='text-muted-foreground'>{t('verification.verificationDetail.viewer.company')}</span>
                          <span>{insurance.company}</span>
                        </div>
                        <div className='flex items-center justify-between'>
                          <span className='text-muted-foreground'>{t('verification.verificationDetail.viewer.policyNumber')}</span>
                          <span className='font-mono text-xs'>{insurance.policyNumber}</span>
                        </div>
                        <div className='flex items-center justify-between'>
                          <span className='text-muted-foreground'>{t('verification.verificationDetail.viewer.validPeriod')}</span>
                          <span>
                            {formatDateOnlyTimestamp(insurance.validFrom, i18n.language || 'de-DE')} – {formatDateOnlyTimestamp(insurance.validTo, i18n.language || 'de-DE')}
                          </span>
                        </div>
                      </>
                    )}
                    {currentDoc.rejectionReason && (
                      <Alert variant='destructive' className='mt-2'>
                        <AlertDescription className='text-xs'>
                          <strong>{t('verification.verificationDetail.viewer.previousRejection')}</strong> {currentDoc.rejectionReason}
                        </AlertDescription>
                      </Alert>
                    )}
                  </div>

                  {/* Document Preview */}
                  {currentDoc.documentUrl && (
                    <div className='border rounded-lg overflow-hidden'>
                      <iframe src={currentDoc.documentUrl} className='w-full h-[600px]' title={t('verification.verificationDetail.viewer.documentPreviewTitle', { type: t(`verification.verificationDetail.docTypes.${currentDocView}`) })} />
                    </div>
                  )}

                  {/* Download Button */}
                  {currentDoc.documentUrl && (
                    <div className='flex justify-end'>
                      <Button variant='outline' size='sm' onClick={() => window.open(currentDoc.documentUrl, '_blank')}>
                        <Icons.Upload className='h-4 w-4 mr-2 rotate-180' />
                        {t('verification.verificationDetail.viewer.download')}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Approve Modal */}
      <Dialog open={showApproveModal} onOpenChange={setShowApproveModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('verification.verificationDetail.modals.approve.title')}</DialogTitle>
            <DialogDescription>{t('verification.verificationDetail.modals.approve.description')}</DialogDescription>
          </DialogHeader>
          <div className='space-y-4'>
            <div className='space-y-2'>
              <Label htmlFor='approve-notes'>{t('verification.verificationDetail.modals.approve.notes')}</Label>
              <Textarea id='approve-notes' value={approveNotes} onChange={(e) => setApproveNotes(e.target.value)} placeholder={t('verification.verificationDetail.modals.approve.notesPlaceholder')} rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant='outline' onClick={() => setShowApproveModal(false)} disabled={processing}>
              {t('personalInfo.actions.cancel')}
            </Button>
            <Button onClick={handleApprove} disabled={processing}>
              {processing ? t('verification.verificationDetail.modals.approve.submitting') : t('verification.verificationDetail.modals.approve.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Request Changes Modal */}
      <Dialog open={showChangesModal} onOpenChange={setShowChangesModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('verification.verificationDetail.modals.requestChanges.title')}</DialogTitle>
            <DialogDescription>{t('verification.verificationDetail.modals.requestChanges.description')}</DialogDescription>
          </DialogHeader>
          <div className='space-y-4'>
            <div className='space-y-2'>
              <Label htmlFor='changes-doctype'>{t('verification.verificationDetail.modals.requestChanges.documentType')}</Label>
              <Select value={changesDocType} onValueChange={(v: any) => setChangesDocType(v)}>
                <SelectTrigger id='changes-doctype'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='approbation'>{t('verification.verificationDetail.docTypes.approbationOnly')}</SelectItem>
                  <SelectItem value='insurance'>{t('verification.verificationDetail.docTypes.insuranceOnly')}</SelectItem>
                  <SelectItem value='both'>{t('verification.verificationDetail.docTypes.both')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className='space-y-2'>
              <Label htmlFor='changes-notes'>{t('verification.verificationDetail.modals.requestChanges.comments')}</Label>
              <Textarea id='changes-notes' value={changesNotes} onChange={(e) => setChangesNotes(e.target.value)} placeholder={t('verification.verificationDetail.modals.requestChanges.commentsPlaceholder')} rows={4} required />
            </div>
          </div>
          <DialogFooter>
            <Button variant='outline' onClick={() => setShowChangesModal(false)} disabled={processing}>
              {t('personalInfo.actions.cancel')}
            </Button>
            <Button onClick={handleRequestChanges} disabled={processing || !changesNotes.trim()}>
              {processing ? t('verification.verificationDetail.modals.requestChanges.submitting') : t('verification.verificationDetail.modals.requestChanges.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Modal */}
      <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('verification.verificationDetail.modals.reject.title')}</DialogTitle>
            <DialogDescription>{t('verification.verificationDetail.modals.reject.description')}</DialogDescription>
          </DialogHeader>
          <div className='space-y-4'>
            <div className='space-y-2'>
              <Label htmlFor='reject-doctype'>{t('verification.verificationDetail.modals.reject.documentType')}</Label>
              <Select value={rejectDocType} onValueChange={(v: any) => setRejectDocType(v)}>
                <SelectTrigger id='reject-doctype'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='approbation'>{t('verification.verificationDetail.docTypes.approbationOnly')}</SelectItem>
                  <SelectItem value='insurance'>{t('verification.verificationDetail.docTypes.insuranceOnly')}</SelectItem>
                  <SelectItem value='both'>{t('verification.verificationDetail.docTypes.both')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className='space-y-2'>
              <Label htmlFor='reject-reason'>{t('verification.verificationDetail.modals.reject.reason')}</Label>
              <Textarea id='reject-reason' value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder={t('verification.verificationDetail.modals.reject.reasonPlaceholder')} rows={4} required />
            </div>
          </div>
          <DialogFooter>
            <Button variant='outline' onClick={() => setShowRejectModal(false)} disabled={processing}>
              {t('personalInfo.actions.cancel')}
            </Button>
            <Button variant='destructive' onClick={handleReject} disabled={processing || !rejectReason.trim()}>
              {processing ? t('verification.verificationDetail.modals.reject.submitting') : t('verification.verificationDetail.modals.reject.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Reminder Modal */}
      <Dialog open={showReminderModal} onOpenChange={setShowReminderModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('verification.verificationDetail.modals.reminder.title')}</DialogTitle>
            <DialogDescription>{t('verification.verificationDetail.modals.reminder.description')}</DialogDescription>
          </DialogHeader>
          <div className='space-y-4'>
            <div className='space-y-2'>
              <Label htmlFor='reminder-message'>{t('verification.verificationDetail.modals.reminder.customMessage')}</Label>
              <Textarea id='reminder-message' value={reminderMessage} onChange={(e) => setReminderMessage(e.target.value)} placeholder={t('verification.verificationDetail.modals.reminder.placeholder')} rows={4} />
            </div>
          </div>
          <DialogFooter>
            <Button variant='outline' onClick={() => setShowReminderModal(false)} disabled={processing}>
              {t('personalInfo.actions.cancel')}
            </Button>
            <Button onClick={handleSendReminder} disabled={processing}>
              {processing ? t('verification.verificationDetail.modals.reminder.submitting') : t('verification.verificationDetail.modals.reminder.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unapprove User Dialog */}
      <UnapproveUserDialog
        userId={userId as any}
        userName={`${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email}
        isOpen={showUnapproveModal}
        onClose={() => setShowUnapproveModal(false)}
        onSuccess={() => {
          setShowUnapproveModal(false);
          void navigate({ to: '/operators/verification' });
        }}
      />
    </div>
  );
};

export const Route = createFileRoute('/_protected/operators/verification/$userId')({
  loader: async () => {
    const { default: i18n } = await import('../i18n');
    await i18n.loadNamespaces?.(['operator']);
    return null;
  },
  component: OperatorVerificationDetail,
});
